import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { useResponseHandler } from '../../../../../hooks/useResponseHandler';
import { storeCreatePlantilla, storeUpdatePlantilla, getPlantillasCategoriesStore } from '../../../../../store/ratings/thunks';
import InputField from '../../../../../components/formFields/InputField';
import TextAreaField from '../../../../../components/formFields/TextAreaField';
import SelectField from '../../../../../components/formFields/selectField';
import AutocompleteField from '../../../../../components/formFields/autocompleteField';

import formInitialValues from '../formik/formModels/formInitialValues';
import plantillasFormModel from '../formik/formModels/plantillasFormModel';
import validationSchema from '../formik/formModels/validationSchema';
import { mapPlantillaFormToApi, mapApiToPlantillaForm } from '../mappers/plantillasMapper';

interface Template {
  id: string;
  name: string;
  message: string;
  category?: string;
  status: number;
}

interface FormPlantillasProps {
  open: boolean;
  handleClose: () => void;
  templateData?: Template | null;
  isViewMode?: boolean;
}

export const FormPlantillas: React.FC<FormPlantillasProps> = ({ open, handleClose, templateData, isViewMode = false }) => {
    
    const dispatch = useDispatch();
    const { handleResponse, ResponseComponents } = useResponseHandler();

    const isEditMode = Boolean(templateData && templateData.id);

    const { templates_for_select } = useSelector((state: any) => state.shiftTemplates);
    const { google_bussinesses_list_for_select } = useSelector((state: any) => state.ratingsDevices);
   

    const {
        formField: { name, message, category, business }
    } = plantillasFormModel;

    const currentInitialValues = isEditMode && templateData
        ? mapApiToPlantillaForm(templateData, templates_for_select, google_bussinesses_list_for_select)
        : formInitialValues;

    const onSubmit = async (values: any, { setSubmitting }: any) => {
        let response;
        const mappedValues = mapPlantillaFormToApi(values);

        if (isEditMode && templateData) {
            response = await dispatch(storeUpdatePlantilla({ ...mappedValues, id: templateData.id }));
        } else {
            response = await dispatch(storeCreatePlantilla(mappedValues));
        }

        await handleResponse(response, isEditMode ? 'editar plantilla' : 'crear plantilla', true);

        setSubmitting(false);

        if (response && response.ok) {
            handleClose();
        }
    };

    return (
        <>
            {ResponseComponents}
            <Dialog 
                open={open} 
                onClose={handleClose} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 3, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(105, 108, 255, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        color: '#696cff'
                    }}>
                        {isViewMode ? <VisibilityOutlinedIcon sx={{ fontSize: 24 }} /> : isEditMode ? <EditOutlinedIcon sx={{ fontSize: 24 }} /> : <SaveOutlinedIcon sx={{ fontSize: 24 }} />}
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#32475CDE', lineHeight: 1.2 }}>
                            {isViewMode ? 'Detalles de la Plantilla' : isEditMode ? 'Editar Plantilla' : 'Crear Plantilla'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#32475C99', mt: 0.5 }}>
                            {isViewMode ? 'Información de la plantilla de respuesta' : isEditMode ? 'Actualiza la información de la plantilla' : 'Agrega una nueva plantilla de respuesta'}
                        </Typography>
                    </Box>
                    
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Formik
                    initialValues={currentInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                    enableReinitialize
                >
                    {({ isSubmitting, isValid, dirty }) => (
                        <Form id={plantillasFormModel.formId}>
                            <DialogContent dividers sx={{ p: 3, borderTop: '1px solid #32475C1F', borderBottom: '1px solid #32475C1F' }}>
                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12 }}>
                                        <InputField
                                            name={name.name}
                                            label={name.label}
                                            fullWidth
                                            autoComplete='off'
                                            placeholder="Ej. Agradecimiento 5 Estrellas"
                                            InputProps={{ readOnly: isViewMode }}
                                        />
                                    </Grid>
                                    
                                    <Grid size={{ xs: 12 }}>
                                        <AutocompleteField
                                            name={category.name}
                                            label={category.label}
                                            fullWidth
                                            options={templates_for_select}
                                            disabled={isViewMode}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12 }}>
                                        <AutocompleteField
                                            name={business.name}
                                            label={business.label}
                                            fullWidth
                                            options={google_bussinesses_list_for_select}
                                            disabled={isViewMode}
                                        />
                                    </Grid>
                                    
                                    <Grid size={{ xs: 12 }}>
                                        <TextAreaField
                                            name={message.name}
                                            label={message.label}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            placeholder="Mensaje automático que se enviará..."
                                            InputProps={{ readOnly: isViewMode }}
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions sx={{ p: 3, pt: 2, pb: 2, backgroundColor: '#F8F9FA' }}>
                                <Button 
                                    onClick={handleClose} 
                                    variant="outlined" 
                                    sx={{ 
                                        color: '#64748B', 
                                        borderColor: '#CBD5E1',
                                        '&:hover': {
                                            borderColor: '#94A3B8',
                                            backgroundColor: '#F1F5F9'
                                        }
                                    }}
                                >
                                    {isViewMode ? 'Cerrar' : 'Cancelar'}
                                </Button>
                                {!isViewMode && (
                                    <Button 
                                        type="submit" 
                                        variant="contained" 
                                        disabled={isSubmitting || !isValid || !dirty}
                                        startIcon={isSubmitting ? null : (isEditMode ? <EditOutlinedIcon /> : <SaveOutlinedIcon />)}
                                        sx={{ 
                                            bgcolor: '#696cff',
                                            '&:hover': { bgcolor: '#5f61e6' }
                                        }}
                                    >
                                        {isSubmitting ? 'Guardando...' : (isEditMode ? 'Actualizar' : 'Guardar')}
                                    </Button>
                                )}
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </>
    );
};
